import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Home, AlertCircle, ArrowLeft } from 'lucide-react';

const THEME = {
    purple: '#836EF9',
    purpleLight: '#A08BFF',
    purpleDark: '#5e17eb',
    yellow: '#FFD700',
    orange: '#FF8C00',
    bg: 'linear-gradient(135deg, #5e17eb 0%, #3e0eb3 50%, #2a0a80 100%)',
    card: 'linear-gradient(145deg, rgba(131, 110, 249, 0.15) 0%, rgba(94, 23, 235, 0.1) 100%)',
    border: 'rgba(255, 215, 0, 0.2)',
};

const styles = {
    container: {
        minHeight: '100vh',
        background: THEME.bg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem',
    } as React.CSSProperties,
    content: {
        maxWidth: '600px',
        textAlign: 'center' as const,
        padding: '3rem',
        background: THEME.card,
        border: `1px solid ${THEME.border}`,
        borderRadius: '24px',
        boxShadow: '0 0 60px rgba(131, 110, 249, 0.3)',
    } as React.CSSProperties,
    logo: {
        width: '120px',
        height: '120px',
        margin: '0 auto 2rem',
        borderRadius: '24px',
        background: `linear-gradient(135deg, ${THEME.purple}, ${THEME.yellow})`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: `0 0 40px ${THEME.purple}60`,
        padding: '1rem',
    } as React.CSSProperties,
    errorCode: {
        fontSize: '6rem',
        fontWeight: 900,
        background: `linear-gradient(135deg, ${THEME.purple}, ${THEME.yellow})`,
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        marginBottom: '1rem',
        lineHeight: 1,
    } as React.CSSProperties,
    title: {
        fontSize: '2rem',
        fontWeight: 700,
        color: '#fff',
        marginBottom: '1rem',
    } as React.CSSProperties,
    description: {
        fontSize: '1.125rem',
        color: '#9ca3af',
        marginBottom: '2.5rem',
        lineHeight: 1.6,
    } as React.CSSProperties,
    buttonGroup: {
        display: 'flex',
        gap: '1rem',
        justifyContent: 'center',
        flexWrap: 'wrap' as const,
    } as React.CSSProperties,
    button: (primary: boolean) => ({
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.5rem',
        padding: '0.875rem 1.75rem',
        borderRadius: '12px',
        fontSize: '1rem',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.2s',
        border: 'none',
        background: primary
            ? `linear-gradient(135deg, ${THEME.purple}, ${THEME.purpleDark})`
            : 'rgba(131, 110, 249, 0.1)',
        color: '#fff',
        boxShadow: primary ? `0 4px 20px ${THEME.purple}40` : 'none',
    } as React.CSSProperties),
    icon: {
        width: '80px',
        height: '80px',
        margin: '0 auto 1.5rem',
    } as React.CSSProperties,
};

const NotFound: React.FC = () => {
    const navigate = useNavigate();

    return (
        <div style={styles.container}>
            <div style={styles.content}>
                {/* Logo */}
                <div style={styles.logo}>
                    <img
                        src="/images/duck.png"
                        alt="Duck Logo"
                        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                        onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                            e.currentTarget.style.display = 'none';
                        }}
                    />
                </div>

                {/* Error Icon */}
                <div style={styles.icon}>
                    <AlertCircle size={80} color={THEME.orange} style={{ opacity: 0.8 }} />
                </div>

                {/* Error Code */}
                <div style={styles.errorCode}>404</div>

                {/* Title */}
                <h1 style={styles.title}>Page Not Found</h1>

                {/* Description */}
                <p style={styles.description}>
                    The page you are looking for doesn't exist or has been moved.
                    Return to the homepage or go back.
                </p>

                {/* Buttons */}
                <div style={styles.buttonGroup}>
                    <button
                        onClick={() => navigate('/')}
                        style={styles.button(true)}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = `0 6px 30px ${THEME.purple}60`;
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = `0 4px 20px ${THEME.purple}40`;
                        }}
                    >
                        <Home size={20} />
                        Back to Home
                    </button>
                    <button
                        onClick={() => navigate(-1)}
                        style={styles.button(false)}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'rgba(131, 110, 249, 0.2)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'rgba(131, 110, 249, 0.1)';
                        }}
                    >
                        <ArrowLeft size={20} />
                        Go Back
                    </button>
                </div>

                {/* Additional Info */}
                <div style={{
                    marginTop: '3rem',
                    padding: '1rem',
                    background: 'rgba(0,0,0,0.2)',
                    borderRadius: '12px',
                    fontSize: '0.875rem',
                    color: '#6b7280',
                }}>
                    <p style={{ margin: 0 }}>
                        If you think this is an error, please let us know.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default NotFound;
